import * as THREE from 'three'
import { MAX_FRAMES_PER_MESH, MM_PER_UNIT, SectionFrame } from './types'
import { AtlasHandle, TilePainter, createAtlas, drawAtlas } from './atlas'
import { meshToFrameMatrix } from './frameMath'
import {
  EngravingAppearance,
  FrameUniformData,
  createProjectorMaterial,
  restoreOriginalMaterial,
  updateProjectorUniforms,
} from './projectorMaterial'

/**
 * Optional per-frame scene decoration — the wireframe and normal arrow an EDITOR draws so
 * an author can see where the projector is, as opposed to only where its paint lands.
 *
 * Injected rather than imported because the Customizer renders the same sections for a
 * shopper and must not draw any of it. Keeping it a hook is what lets one renderer serve
 * both without a `isAdmin` flag threaded through the drawing code.
 */
export interface FrameDecoration {
  update(frame: SectionFrame, selected: boolean): void
  removeFromParent(): void
  dispose(): void
}

export interface FrameRendererOptions {
  /** Called once per frame that needs a decoration. Omit for a plain render. */
  createDecoration?: () => FrameDecoration & THREE.Object3D
  /**
   * What goes inside each section's tile. Defaults to the authoring label.
   *
   * The Customizer overrides it to draw the customer's artwork. Everything that decides
   * WHERE a section lands — the frame maths, the tiling, the shader — stays identical
   * either way; only the pixels differ.
   */
  paintTile?: TilePainter
  /** Tile resolution. 512 suits a label; artwork wants 1024 or more. */
  tilePx?: number
  /**
   * How the engraving on a given mesh should LOOK — its material, tint and finish.
   *
   * Omit it and sections render as the authoring mask, which is what the admin portal
   * wants. The Customizer supplies one per mesh, resolved the same way the unrolled path
   * resolves it, so a section looks the same whichever route painted it.
   */
  appearanceFor?: (meshName: string) => EngravingAppearance | null
}

/**
 * Keeps the scene in step with the frame list.
 *
 * State this owns — per-mesh atlases, projector materials, the frame wireframes — is
 * derived entirely from the frames passed to sync(), so the React side can hold the
 * sections as plain data and never reach into three.
 */
export class FrameRenderer {
  private atlases = new Map<string, AtlasHandle>()
  private helpers = new Map<string, FrameDecoration & THREE.Object3D>()
  private helperRoot = new THREE.Group()
  private productRoot: THREE.Object3D | null = null
  private createDecoration?: () => FrameDecoration & THREE.Object3D
  private paintTile?: TilePainter
  private tilePx?: number
  private appearanceFor?: (meshName: string) => EngravingAppearance | null

  constructor(options: FrameRendererOptions = {}) {
    this.createDecoration = options.createDecoration
    this.paintTile = options.paintTile
    this.tilePx = options.tilePx
    this.appearanceFor = options.appearanceFor
  }

  /**
   * Change the tile resolution.
   *
   * The atlases are dropped so the next sync rebuilds them at the new size; the projector
   * materials are left alone, so this costs a texture rebuild and not a shader recompile.
   * That matters because the intended caller is the quality switch that fires on every
   * interaction — a consumer drawing real artwork wants small tiles while something is
   * being dragged and sharp ones once it settles.
   */
  setTilePx(px: number): void {
    if (px === this.tilePx) return
    this.tilePx = px
    this.atlases.forEach((atlas) => atlas.dispose())
    this.atlases.clear()
  }

  attach(productRoot: THREE.Object3D): void {
    this.detach()
    this.productRoot = productRoot
    productRoot.add(this.helperRoot)
  }

  detach(): void {
    if (!this.productRoot) return
    this.forEachMesh((mesh) => restoreOriginalMaterial(mesh))
    this.helpers.forEach((helper) => {
      helper.removeFromParent()
      helper.dispose()
    })
    this.helpers.clear()
    this.atlases.forEach((atlas) => atlas.dispose())
    this.atlases.clear()
    this.helperRoot.removeFromParent()
    this.productRoot = null
  }

  /**
   * Rebuild everything the frames imply.
   *
   * Cheap enough to run on every edit: the per-mesh atlas is a handful of canvas draws,
   * and the projector material is only cloned the first time a mesh acquires a section,
   * so dragging a size slider updates uniforms and one canvas rather than recompiling
   * a shader.
   */
  sync(frames: SectionFrame[], selectedId: string | null): void {
    const root = this.productRoot
    if (!root) return

    const byMesh = new Map<string, SectionFrame[]>()
    frames.forEach((frame) => {
      const list = byMesh.get(frame.meshName) ?? []
      if (list.length < MAX_FRAMES_PER_MESH) list.push(frame)
      byMesh.set(frame.meshName, list)
    })

    // Meshes that lost their last section go back to the material they arrived with.
    this.forEachMesh((mesh) => {
      if (byMesh.has(mesh.name)) return
      restoreOriginalMaterial(mesh)
      this.atlases.get(mesh.name)?.dispose()
      this.atlases.delete(mesh.name)
    })

    byMesh.forEach((meshFrames, meshName) => {
      const mesh = root.getObjectByName(meshName) as THREE.Mesh | undefined
      if (!mesh?.isMesh) return

      let atlas = this.atlases.get(meshName)
      if (!atlas) {
        atlas = createAtlas({ tilePx: this.tilePx })
        this.atlases.set(meshName, atlas)
      }
      drawAtlas(atlas, meshFrames, selectedId, this.paintTile)

      const isProjector = !!(mesh.material as THREE.Material)?.userData?.v3Uniforms
      if (!isProjector) mesh.material = createProjectorMaterial(mesh)

      const uniformData: FrameUniformData[] = meshFrames.map((frame, index) => ({
        matrix: meshToFrameMatrix(frame, mesh, root),
        size: new THREE.Vector2(frame.width / MM_PER_UNIT, frame.height / MM_PER_UNIT),
        depth: frame.depth / MM_PER_UNIT,
        wrapRadius: frame.wrapRadius / MM_PER_UNIT,
        minCos: Math.cos(THREE.MathUtils.degToRad(Math.min(frame.maxAngleDeg ?? 55, 89))),
        tile: index,
      }))

      const highlight = meshFrames.findIndex((f) => f.id === selectedId)
      updateProjectorUniforms(
        mesh.material as THREE.Material,
        atlas.texture,
        uniformData,
        highlight,
        this.appearanceFor?.(meshName)
      )
    })

    this.syncHelpers(frames, selectedId)
  }

  private syncHelpers(frames: SectionFrame[], selectedId: string | null): void {
    const live = new Set(frames.map((f) => f.id))
    this.helpers.forEach((helper, id) => {
      if (live.has(id)) return
      helper.removeFromParent()
      helper.dispose()
      this.helpers.delete(id)
    })

    if (!this.createDecoration) return

    frames.forEach((frame) => {
      let helper = this.helpers.get(frame.id)
      if (!helper) {
        helper = this.createDecoration!()
        this.helpers.set(frame.id, helper)
        this.helperRoot.add(helper)
      }
      helper.update(frame, frame.id === selectedId)
    })
  }

  private forEachMesh(fn: (mesh: THREE.Mesh) => void): void {
    this.productRoot?.traverse((child) => {
      const mesh = child as THREE.Mesh
      if (mesh.isMesh) fn(mesh)
    })
  }
}
